/**
 * <TutorLiveMosaic> — la mosaïque du live du masteur : tout le groupe, sur tout l'écran (organism).
 *
 * L'ancienne grille (`TutorGridLayoutM`, ouverte par `LayoutToggleM` dans la barre du bas), reprise au
 * round 3 (Wajih : « un peu comme avant ») : un mode de tout l'écran, pas un onglet du centre. La règle
 * de colonnes d'aujourd'hui (1 · 2 · 2 · 2 · 3…), la dernière rangée centrée, chaque case à sa part de
 * la hauteur. Chaque élève garde ce que sa vignette dit : sa puce la plus urgente, le liseré vert quand
 * il parle, et sa bascule Dialogue · Concentré dessous. Toucher sa vidéo le regarde (`onSelect`) : la barre
 * referme la mosaïque, c'est l'écran qui le décide.
 *
 * Round 4 (Wajih : « l'activité en cours de chaque élève doit être visible ») : chaque case est la carte de
 * l'élève de la pellicule (`<TutorStudentTile>`, qui remplit sa case) — sa vidéo, sa bascule, l'activité qu'il
 * a devant lui, qui s'ouvre au centre, et ce qui attend le masteur chez lui.
 *
 * Le masteur s'y voit (Wajih, weekly du 03/11 : « comme dans l'ancienne mosaïque ») : sa vignette (`me`) est la
 * dernière case, « Toi ». La mosaïque recouvre la pellicule, qui la portait ; sans elle, il ne se voyait plus.
 * Les autres tuteurs de la séance (`coTutors`) ont leur case avant la sienne, comme dans la pellicule.
 *
 * **Elle arrive, elle ne saute pas** : à l'ouverture, la grille entre en fondu en se dépliant légèrement (`entree`) ;
 * sous « moins d'animation », un fondu seul. Elle remplace la pellicule et le centre : pas de colonne de gauche.
 *
 * Contrôlée : les élèves et leurs modes arrivent en props, chaque geste repart.
 */
import React, { useMemo } from "react"
import { Animated, View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { type UILiveParticipant, type UISoundMode } from "../../../ports/primary/ui/types/studentLive"
import { type UITutorLiveStudent } from "../../../ports/primary/ui/types/tutorLive"
import { tutorMeParticipant } from "../../molecules/TutorStudentVideo"
import { TutorStudentTile } from "../../molecules/TutorStudentTile"
import { VideoTile } from "../../molecules/VideoTile"
import { useFadeIn } from "../../hooks/useFadeIn"
import { useReduceMotion } from "../../hooks/useReduceMotion"

export type TutorLiveMosaicProps = {
    students: UITutorLiveStudent[]
    /** Le masteur : sa vignette, la dernière case. Absent : les élèves seuls. */
    me?: UILiveParticipant
    /** Les autres tuteurs connectés à la séance, une case chacun avant la mienne. Défaut : aucun. */
    coTutors?: UILiveParticipant[]
    onSelect: (studentId: string) => void
    onChangeMode: (studentId: string, mode: UISoundMode) => void
    /** Ouvrir au centre l'activité qu'un élève a devant lui. Absent : la barre se lit, sans geste. */
    onOpenActivity?: (studentId: string, activityId: string) => void
    /** Le fil d'un élève touché : la vue de l'élève, son onglet « Fil » au centre. */
    onOpenThread?: (studentId: string, eventId?: string) => void
    /** L'élève dont la bascule part. */
    modePendingId?: string
    /** La vraie piste d'un élève. Absente : sa photo, ou son visage. */
    renderVideo?: (participantId: string) => React.ReactNode
    testID?: string
}

/** D'où part la grille à son entrée : un peu plus petite, elle se déplie vers sa place. */
const ENTRY_SCALE = 0.96

/** Les colonnes de la grille d'aujourd'hui, selon le nombre d'élèves (`tutorGridLayout`). */
const GRID_COLUMNS = [1, 1, 2, 2, 2, 3, 3, 3, 3, 3, 4]

export function TutorLiveMosaic(props: TutorLiveMosaicProps) {
    const { students, me, coTutors, onSelect, onChangeMode, onOpenActivity, onOpenThread, modePendingId } = props
    const { renderVideo, testID } = props
    const cells = students.length + (coTutors?.length ?? 0) + (me != null ? 1 : 0)
    const columns = GRID_COLUMNS[Math.min(cells, GRID_COLUMNS.length - 1)] ?? 4
    const rows = Math.max(1, Math.ceil(cells / columns))
    const meVideo = me != null ? renderVideo?.(me.id) : undefined
    const reduceMotion = useReduceMotion()
    // L'entrée : un fondu et une échelle de 0,96 ; en mouvement réduit, le fondu seul.
    const entry = useFadeIn(true, { duration: "entree", fadeWhenReduced: true })
    const scale = useMemo(() => entry.interpolate({ inputRange: [0, 1], outputRange: [ENTRY_SCALE, 1] }), [entry])
    return (
        <Animated.View
            style={[styles.grid, { opacity: entry, transform: reduceMotion ? [] : [{ scale }] }]}
            testID={testID}
        >
            {students.map((student) => (
                <View key={student.id} style={styles.cell(columns, rows)}>
                    <TutorStudentTile
                        student={student}
                        onSelect={() => onSelect(student.id)}
                        onChangeMode={(mode) => onChangeMode(student.id, mode)}
                        modePending={student.id === modePendingId}
                        video={renderVideo?.(student.id)}
                        onOpenActivity={
                            onOpenActivity != null ? (activityId) => onOpenActivity(student.id, activityId) : undefined
                        }
                        onOpenThread={onOpenThread != null ? (eventId) => onOpenThread(student.id, eventId) : undefined}
                    />
                </View>
            ))}
            {coTutors?.map((tutor) => {
                const video = renderVideo?.(tutor.id)
                return (
                    <View key={tutor.id} style={styles.cell(columns, rows)}>
                        <VideoTile
                            participant={tutorMeParticipant(tutor, video)}
                            size="tile"
                            video={video}
                            micMark="name"
                        />
                    </View>
                )
            })}
            {me != null && (
                <View style={styles.cell(columns, rows)}>
                    <VideoTile
                        participant={tutorMeParticipant(me, meVideo)}
                        size="tile"
                        me
                        video={meVideo}
                        micMark="name"
                    />
                </View>
            )}
        </Animated.View>
    )
}

const styles = StyleSheet.create((theme) => ({
    grid: {
        flex: 1,
        minHeight: 0,
        flexDirection: "row",
        flexWrap: "wrap",
        justifyContent: "center",
        alignContent: "center",
        gap: theme.spacing["3"],
    },
    /** Chaque case prend sa part de la largeur ET de la hauteur : la mosaïque tient toujours dans l'écran. */
    cell: (columns: number, rows: number) => ({
        width: `${Math.floor(100 / columns) - 2}%` as const,
        height: `${Math.floor(100 / rows) - 3}%` as const,
    }),
}))
